/**
 * 에셋 프레임 Context
 *
 * 목적:
 * - 에셋 ID별로 추출된 프레임 데이터를 전역으로 공유
 * - 비디오 에셋이 캐싱되면 자동으로 프레임 추출 시작
 * - 여러 비디오 아이템이 같은 에셋을 참조해도 프레임은 한 번만 추출
 *
 * 사용 방법:
 * - AssetFramesProvider로 에디터 감싸기
 * - useAssetFrames(assetId)로 특정 에셋의 프레임 조회
 */

'use client';

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { VideoAsset } from '../assets/assets';
import {
  AssetFrameData,
  cleanupFrames,
  DEFAULT_FRAME_INTERVAL_SECONDS,
  extractFramesFromAsset,
  ExtractedFrame,
  ExtractionStatus,
  PriorityRange,
} from '../caching/asset-frame-extractor';
import {
  checkIfLocalUrlIsStillValid,
  useLocalUrls,
} from '../caching/load-to-blob-url';
import { VideoItem } from '../items/video/video-item-type';
import { useAllItems, useAssets, useFps } from '../utils/use-context';

// Context 타입 정의
interface AssetFramesContextValue {
  // 에셋 ID로 프레임 데이터 조회
  getFrameData: (assetId: string) => AssetFrameData | null;
  // 에셋 ID로 프레임 추출 상태 조회
  getStatus: (assetId: string) => ExtractionStatus;
  // 에셋 ID로 프레임 배열 조회
  getFrames: (assetId: string) => ExtractedFrame[];
  // 프레임 추출 간격 (초)
  frameInterval: number;
  // 프레임 추출 간격 변경
  setFrameInterval: (interval: number) => void;
}

const AssetFramesContext = createContext<AssetFramesContextValue | null>(null);

// Provider Props
interface AssetFramesProviderProps {
  children: React.ReactNode;
  initialFrameInterval?: number;
}

/**
 * 에셋 프레임 Provider
 */
export function AssetFramesProvider({
  children,
  initialFrameInterval = DEFAULT_FRAME_INTERVAL_SECONDS,
}: AssetFramesProviderProps) {
  // 프레임 데이터 저장소
  const [frameDataMap, setFrameDataMap] = useState<Map<string, AssetFrameData>>(
    new Map(),
  );

  // 프레임 추출 간격
  const [frameInterval, setFrameInterval] = useState(initialFrameInterval);

  // 추출 중인 에셋 ID 추적 (중복 추출 방지)
  const extractingAssetsRef = useRef<Set<string>>(new Set());

  // AbortController 저장소
  const abortControllersRef = useRef<Map<string, AbortController>>(new Map());

  // 마지막으로 사용된 blob URL 저장 (변경 감지용)
  const lastBlobUrlsRef = useRef<Map<string, string>>(new Map());

  // 에셋별 재시도 횟수 추적 (무한 재시도 방지)
  const retryCountRef = useRef<Map<string, number>>(new Map());
  const MAX_RETRY_COUNT = 3;

  // frameDataMap ref (콜백에서 최신 값 접근용)
  const frameDataMapRef = useRef(frameDataMap);
  frameDataMapRef.current = frameDataMap;

  // 에셋 및 로컬 URL 정보
  const { assets } = useAssets();
  const localUrls = useLocalUrls();
  const { items } = useAllItems();
  const { fps } = useFps();

  // 비디오 에셋만 필터링
  const videoAssets = useMemo(() => {
    return Object.values(assets).filter(
      (asset): asset is VideoAsset => asset.type === 'video',
    );
  }, [assets]);

  // 비디오 아이템만 필터링
  const videoItems = useMemo(() => {
    return Object.values(items).filter(
      (item): item is VideoItem => item.type === 'video',
    );
  }, [items]);

  // 에셋 ID별로 사용 중인 비디오 아이템의 시간 범위 계산
  const getPriorityRangesForAsset = useCallback(
    (assetId: string): PriorityRange[] => {
      const ranges: PriorityRange[] = [];

      for (const item of videoItems) {
        if (item.assetId !== assetId) continue;

        // 비디오 아이템의 원본 비디오 시간 범위 계산
        const startInSeconds = item.videoStartFromInSeconds;
        const durationInSeconds =
          (item.durationInFrames / fps) * item.playbackRate;
        const endInSeconds = startInSeconds + durationInSeconds;

        ranges.push({
          start: startInSeconds,
          end: endInSeconds,
        });
      }

      return ranges;
    },
    [videoItems, fps],
  );

  // 프레임 추출 함수
  const extractFramesForAsset = useCallback(
    async (asset: VideoAsset, blobUrl: string, forceRetry: boolean = false) => {
      const assetId = asset.id;

      // 이미 추출 중이면 스킵
      if (extractingAssetsRef.current.has(assetId)) {
        return;
      }

      // 이미 완료된 경우 스킵 (forceRetry가 아닌 경우)
      const existingData = frameDataMapRef.current.get(assetId);
      if (
        !forceRetry &&
        existingData?.status === 'done' &&
        existingData.frames.length > 0
      ) {
        return;
      }

      // blob URL 저장
      lastBlobUrlsRef.current.set(assetId, blobUrl);

      // 추출 시작 표시
      extractingAssetsRef.current.add(assetId);

      // AbortController 생성
      const abortController = new AbortController();
      abortControllersRef.current.set(assetId, abortController);

      // 초기 상태 설정
      setFrameDataMap((prev) => {
        const newMap = new Map(prev);
        newMap.set(assetId, {
          assetId,
          status: 'extracting',
          frames: [],
          durationInSeconds: asset.durationInSeconds,
          aspectRatio: asset.width / asset.height,
        });
        return newMap;
      });

      try {
        // 현재 타임라인에 표시 중인 비디오 아이템의 시간 범위를 우선 추출
        const priorityRanges = getPriorityRangesForAsset(assetId);

        const frames = await extractFramesFromAsset({
          assetId,
          blobUrl,
          durationInSeconds: asset.durationInSeconds,
          aspectRatio: asset.width / asset.height,
          intervalInSeconds: frameInterval,
          priorityRanges,
          signal: abortController.signal,
          onFrame: (frame) => {
            setFrameDataMap((prev) => {
              const newMap = new Map(prev);
              const currentData = newMap.get(assetId);
              if (currentData) {
                const newFrames = [...currentData.frames, frame].sort(
                  (a, b) => a.timestampInSeconds - b.timestampInSeconds,
                );
                newMap.set(assetId, {
                  ...currentData,
                  frames: newFrames,
                });
              }
              return newMap;
            });
          },
        });

        // 완료 상태 설정
        setFrameDataMap((prev) => {
          const newMap = new Map(prev);
          newMap.set(assetId, {
            assetId,
            status: 'done',
            frames,
            durationInSeconds: asset.durationInSeconds,
            aspectRatio: asset.width / asset.height,
          });
          return newMap;
        });

        // 프레임 추출 성공 시 재시도 카운터 리셋
        if (frames.length > 0) {
          retryCountRef.current.delete(assetId);
        }
      } catch (error) {
        // 취소된 경우는 무시
        if (abortController.signal.aborted) {
          return;
        }

        // 에러 상태 설정
        setFrameDataMap((prev) => {
          const newMap = new Map(prev);
          newMap.set(assetId, {
            assetId,
            status: 'error',
            frames: [],
            error: error instanceof Error ? error : new Error(String(error)),
            durationInSeconds: asset.durationInSeconds,
            aspectRatio: asset.width / asset.height,
          });
          return newMap;
        });
      } finally {
        // 추출 완료 표시
        extractingAssetsRef.current.delete(assetId);
        abortControllersRef.current.delete(assetId);
      }
    },
    [frameInterval, getPriorityRangesForAsset],
  );

  // 비디오 에셋이 캐싱되면 프레임 추출 시작
  useEffect(() => {
    // 디버그용 전역 함수 노출 (개발 환경에서만)
    if (
      typeof window !== 'undefined' &&
      process.env.NODE_ENV === 'development'
    ) {
      (window as unknown as Record<string, unknown>).__ASSET_FRAMES_DEBUG__ = {
        videoAssets,
        localUrls,
        frameDataMap: Object.fromEntries(frameDataMap),
        extractFramesForAsset,
        frameInterval,
      };
    }

    const localAbort = new AbortController();

    const processAssets = async () => {
      for (const asset of videoAssets) {
        if (localAbort.signal.aborted) return;

        const blobUrl = localUrls[asset.id] || null;

        // blob URL이 없으면 스킵 (V1과 동일하게 blob URL 필요)
        if (!blobUrl) continue;

        // ref를 통해 최신 상태 확인 (frameDataMap dependency 제거를 위해)
        const existingData = frameDataMapRef.current.get(asset.id);
        const lastBlobUrl = lastBlobUrlsRef.current.get(asset.id);
        const blobUrlChanged = lastBlobUrl && lastBlobUrl !== blobUrl;

        // blob URL이 변경되었고 에러 상태이거나 프레임이 0개이면 재시도
        if (
          blobUrlChanged &&
          (existingData?.status === 'error' ||
            (existingData?.status === 'done' &&
              existingData.frames.length === 0))
        ) {
          extractFramesForAsset(asset, blobUrl, true);
          continue;
        }

        // 아직 추출되지 않은 에셋 또는 추출 완료했지만 프레임이 0개인 에셋 처리
        const isEmptyDone =
          existingData?.status === 'done' && existingData.frames.length === 0;
        const shouldExtract =
          !existingData ||
          (existingData.status !== 'done' &&
            existingData.status !== 'extracting') ||
          isEmptyDone;

        // 0 프레임 재시도 시 횟수 제한
        if (isEmptyDone) {
          const retryCount = retryCountRef.current.get(asset.id) ?? 0;
          if (retryCount >= MAX_RETRY_COUNT) {
            continue;
          }
          retryCountRef.current.set(asset.id, retryCount + 1);
        }

        if (shouldExtract) {
          if (localAbort.signal.aborted) return;
          // blob URL 유효성 검사 (유효하지 않으면 재생성 후 localUrls 업데이트됨)
          const isValid = await checkIfLocalUrlIsStillValid(blobUrl, asset);
          if (localAbort.signal.aborted) return;
          if (!isValid) {
            continue; // localUrls 업데이트되면 useEffect가 다시 트리거됨
          }

          extractFramesForAsset(asset, blobUrl, isEmptyDone);
        }
      }
    };

    // local URL 전환 직후의 메인 스레드 부하를 줄이기 위해 프레임 추출 2초 지연
    const delayTimer = setTimeout(processAssets, 2000);
    return () => {
      clearTimeout(delayTimer);
      localAbort.abort();
    };
    // frameDataMap을 dependency에서 제거하여 불필요한 useEffect 트리거 방지
    // frameDataMapRef.current를 통해 최신 상태 접근
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoAssets, localUrls, extractFramesForAsset, frameInterval]);

  // 컴포넌트 언마운트 시 정리 (빈 의존성 배열로 마운트/언마운트 시에만 실행)
  useEffect(() => {
    return () => {
      // 모든 추출 취소
      abortControllersRef.current.forEach((controller) => {
        controller.abort();
      });
      abortControllersRef.current.clear();

      // 모든 ImageBitmap 정리 (ref를 통해 최신 값 접근)
      frameDataMapRef.current.forEach((data) => {
        cleanupFrames(data.frames);
      });
    };
  }, []);

  // Context 값
  const contextValue = useMemo<AssetFramesContextValue>(
    () => ({
      getFrameData: (assetId: string) => frameDataMap.get(assetId) ?? null,
      getStatus: (assetId: string) =>
        frameDataMap.get(assetId)?.status ?? 'idle',
      getFrames: (assetId: string) => frameDataMap.get(assetId)?.frames ?? [],
      frameInterval,
      setFrameInterval,
    }),
    [frameDataMap, frameInterval],
  );

  return (
    <AssetFramesContext.Provider value={contextValue}>
      {children}
    </AssetFramesContext.Provider>
  );
}

/**
 * 에셋 프레임 Context 사용 훅
 */
export function useAssetFramesContext(): AssetFramesContextValue {
  const context = useContext(AssetFramesContext);
  if (!context) {
    throw new Error(
      'useAssetFramesContext must be used within AssetFramesProvider',
    );
  }
  return context;
}

/**
 * 특정 에셋의 프레임 데이터 조회 훅
 */
export function useAssetFrames(assetId: string): {
  frames: ExtractedFrame[];
  status: ExtractionStatus;
  isExtracting: boolean;
  isDone: boolean;
} {
  const { getFrames, getStatus } = useAssetFramesContext();

  const frames = getFrames(assetId);
  const status = getStatus(assetId);

  return {
    frames,
    status,
    isExtracting: status === 'extracting',
    isDone: status === 'done',
  };
}

/**
 * 프레임 추출 간격 설정 훅
 */
export function useFrameInterval(): {
  frameInterval: number;
  setFrameInterval: (interval: number) => void;
} {
  const { frameInterval, setFrameInterval } = useAssetFramesContext();
  return { frameInterval, setFrameInterval };
}
